/**
 * The application shell every screen renders inside.
 *
 * Desktop: shadcn's sidebar (collapses to an icon rail) + a sticky header.
 * Mobile (<768px): the sidebar becomes an off-canvas sheet behind the header's
 * trigger, and a bottom tab bar carries the primary destinations.
 */
import { useRouterState } from '@tanstack/react-router'
import { FlaskConical, Search } from 'lucide-react'
import { type CSSProperties, type ReactNode, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { SidebarInset, SidebarProvider, SidebarTrigger, useSidebar } from '@/components/ui/sidebar'
import { Switch } from '@/components/ui/switch'
import { featuresById } from '@/core/system'
import {
  useSaveSidebarWidth,
  useSidebarWidthPreference,
} from '@/features/auth/use-sidebar-width-preference'
import { pageTitle } from '@/lib/brand'
import { useIsMobile, useSizeClass } from '@/lib/hooks'
import { type AccountSummary, AppSidebar } from './app-sidebar'
import { MobileTabBar } from './mobile-tab-bar'
import { isNavItemActive, type NavItem, navItems, titleFromPathname } from './nav-config'

/**
 * Reconciles the sidebar's width with the person's saved preference — server
 * wins over whatever the pre-paint cache guessed. Renders nothing; mounted
 * once inside `<SidebarProvider>` so it can reach `useSidebar()`.
 */
function SidebarWidthReconciler({ signedIn }: { signedIn: boolean }) {
  const { sidebarResizable, setSidebarWidth } = useSidebar()
  const { data } = useSidebarWidthPreference(signedIn && sidebarResizable)
  useEffect(() => {
    if (typeof data?.value === 'number') setSidebarWidth(data.value)
  }, [data, setSidebarWidth])
  return null
}

export interface AppShellProps {
  children: ReactNode
  items?: NavItem[]
  counts?: Record<string, number>
  onOpenCommandPalette?: () => void
  onSignOut?: () => void
  /** The signed-in person. Undefined while it is still loading. */
  account?: AccountSummary
  /**
   * Test mode. `on` shows golden records and unfinished features; `canToggle`
   * is false when there is no real data to switch to (mock mode).
   */
  testMode?: { on: boolean; canToggle: boolean; onChange: (on: boolean) => void }
  /**
   * A bar pinned above everything — the impersonation bar (ADR impersonation).
   * The sidebar and the sticky header move down to make room for it.
   */
  banner?: ReactNode
  /**
   * The Sandbox strip (docs/ENVIRONMENTS.md), pinned above `banner` when both
   * are showing. Each bar is 2.75rem; the header keeps its 64px beneath them.
   */
  environmentBanner?: ReactNode
  /**
   * A notice at the top of every page's content, in the flow (not pinned) —
   * the Admins' "a webhook has gone quiet" banner (docs/INTEGRATIONS.md
   * "Data health"). Renders nothing when there is nothing to say.
   */
  notice?: ReactNode
  /**
   * Shell furniture pinned over the page — the assistant's bubble. Rendered
   * outside <main>, because it belongs to the shell, not to any one screen.
   */
  floating?: ReactNode
  /**
   * Content centered in the sticky header, between the title and Search. A
   * route puts something here with `useHeaderCenterSlot` (header-slot.tsx);
   * most screens leave it unset (DESIGN.md "Header center slot").
   */
  headerCenter?: ReactNode
  /** Help beside Search — a page's "Take the tour" (src/features/shared/tours). */
  help?: ReactNode
}

export function AppShell({
  children,
  items = navItems,
  counts,
  onOpenCommandPalette,
  onSignOut,
  account,
  testMode,
  banner,
  notice,
  environmentBanner,
  floating,
  headerCenter,
  help,
}: AppShellProps) {
  const isMobile = useIsMobile()
  const sizeClass = useSizeClass()
  // Only features that really work appear in navigation, unless Test mode is
  // on. `status` comes from src/core/system/features.ts — the same registry the
  // System page shows, so there is one answer to "is this real?".
  const showUnfinished = testMode?.on ?? true
  items = items.filter((item) => showUnfinished || featuresById.get(item.id)?.status !== 'scaffold')
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const active = items.find((item) => isNavItemActive(pathname, item.url))
  const title = active?.title ?? titleFromPathname(pathname)

  // The document title is always "{Page} · {App}".
  useEffect(() => {
    document.title = pageTitle(title)
  }, [title])

  // Saves a resize once it settles (drag end, a key press, the double-click
  // reset) — debounced, never per pixel. src/core/preferences.
  const saveSidebarWidth = useSaveSidebarWidth()

  // Bars pinned above the whole app, stacked: Sandbox first, then
  // impersonation. The sidebar and header move down by exactly their height.
  const bars = [environmentBanner, banner].filter(Boolean).length
  const barsHeight = `${bars * 2.75}rem`

  return (
    // docs/LAYOUT.md: the sidebar shows labels only when there is room for
    // them — an icon rail between 768 and 1024, full width at 1024 and up.
    <SidebarProvider
      defaultOpen={sizeClass === 'expanded'}
      onWidthCommit={saveSidebarWidth}
      style={
        bars > 0
          ? ({ '--top-banner': barsHeight, paddingTop: barsHeight } as CSSProperties)
          : undefined
      }
    >
      <SidebarWidthReconciler signedIn={account !== undefined} />
      {bars > 0 ? (
        <div className="fixed inset-x-0 top-0 z-40 flex flex-col">
          {environmentBanner}
          {banner}
        </div>
      ) : null}
      <AppSidebar items={items} counts={counts} onSignOut={onSignOut} account={account} />

      {/* min-w-0: a flex item will not shrink below its longest unbreakable line
          by default, so one long record title widened the whole page and made it
          scroll sideways. Caught by the golden record with a maximum-length title. */}
      <SidebarInset className="min-w-0">
        <header className="bg-sidebar/80 sticky top-(--top-banner) z-10 flex h-16 shrink-0 items-center gap-2 border-b backdrop-blur">
          <div className="flex w-full items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 data-[orientation=vertical]:h-4" />
            <h1 className="text-lg font-semibold">{title}</h1>

            {/* Centered between the title and Search (header-slot.tsx).
                `mx-auto` rather than true absolute centering, so it never
                overlaps the sidebar trigger at narrow widths; it still reads
                as "the middle" once the title and Search have taken their own
                space. The content itself is responsible for staying compact
                at 375px. */}
            {headerCenter ? <div className="mx-auto shrink-0">{headerCenter}</div> : null}

            {/* A fixed max-width, not a spacing-scale value — the same kind of
                deliberate content width as the dialogs (docs/LAYOUT.md §3b).
                Grows with `flex-1` up to that cap, then `ml-auto` absorbs
                whatever is left so it still sits flush against Test mode. */}
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenCommandPalette}
              // Named by its own word: a phone hides "Search…" from sight only.
              aria-keyshortcuts="Meta+K"
              className="text-muted-foreground ml-auto min-w-0 justify-start gap-2 max-md:size-11 max-md:justify-center md:flex-1 md:max-w-xs lg:max-w-[520px]"
            >
              <Search className="size-4 shrink-0" aria-hidden="true" />
              <span className="truncate max-sm:sr-only">Search…</span>
              <kbd className="bg-muted text-foreground/70 pointer-events-none ml-auto hidden shrink-0 rounded px-2 py-1 font-mono text-xs sm:inline">
                ⌘K
              </kbd>
            </Button>
            {help}

            {testMode?.canToggle ? (
              // A label, so the whole "Test mode" text is the tap target, not
              // just the small switch — 44px tall per docs/LAYOUT.md.
              <label
                htmlFor="test-mode-switch"
                className="flex h-11 cursor-pointer items-center gap-2 rounded-lg px-2 text-sm font-semibold"
              >
                {/* Always identifiable: the flask (the same one as the banner) on
                    a phone, the words where there is room. A bare switch read as
                    missing. */}
                <FlaskConical
                  className="size-4 text-muted-foreground sm:hidden"
                  aria-hidden="true"
                />
                <span className="hidden sm:inline">Test mode</span>
                <Switch
                  id="test-mode-switch"
                  checked={testMode.on}
                  onCheckedChange={testMode.onChange}
                  aria-label="Test mode"
                />
              </label>
            ) : null}
          </div>
        </header>

        {/* The window scrolls, not main: main only clips anything too wide. A box
            that could scroll would stop a table's header sticking under the bar. */}
        <main
          className="min-h-0 flex-1 overflow-x-clip p-4 md:p-6"
          style={
            isMobile ? { paddingBottom: 'calc(5rem + env(safe-area-inset-bottom))' } : undefined
          }
        >
          {/* Unmistakable, so test records are never mistaken for real ones. */}
          {testMode?.on && testMode.canToggle ? (
            <div className="mb-6 flex flex-col gap-3 rounded-lg border-2 border-warning bg-warning/10 px-4 py-3 md:flex-row md:items-center md:px-6">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <FlaskConical
                  className="mt-0.5 size-5 shrink-0 text-warning-strong"
                  aria-hidden="true"
                />
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-sm font-semibold">
                    Test mode — these are golden test records
                  </span>
                  <span className="max-w-measure text-sm text-muted-foreground">
                    Not your real data, and nothing you do here changes it. Test records reset
                    whenever the app is updated. Unfinished features are shown too.
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                className="self-start md:shrink-0"
                onClick={() => testMode.onChange(false)}
              >
                Show real data
              </Button>
            </div>
          ) : null}
          {notice}
          {children}
        </main>

        {isMobile ? (
          <MobileTabBar items={items} counts={counts} account={account} onSignOut={onSignOut} />
        ) : null}
      </SidebarInset>
      {floating}
    </SidebarProvider>
  )
}
